import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface EditJustificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  justification?: {
    justificationText?: string | null;
    codeBookTitle?: string | null;
    chapter?: string | null;
    snippet?: string | null;
  } | null;
  onSave: (data: {
    justificationText: string;
    codeBookTitle?: string;
    chapter?: string;
    snippet?: string;
  }) => void;
}

export function EditJustificationDialog({
  open,
  onOpenChange,
  justification,
  onSave,
}: EditJustificationDialogProps) {
  const [justificationText, setJustificationText] = useState(justification?.justificationText || '');
  const [codeBookTitle, setCodeBookTitle] = useState(justification?.codeBookTitle || '');
  const [chapter, setChapter] = useState(justification?.chapter || '');
  const [snippet, setSnippet] = useState(justification?.snippet || '');

  // Sync state when dialog opens or justification changes
  useEffect(() => {
    if (open) {
      setJustificationText(justification?.justificationText || '');
      setCodeBookTitle(justification?.codeBookTitle || '');
      setChapter(justification?.chapter || '');
      setSnippet(justification?.snippet || '');
    }
  }, [open, justification]);

  const handleSave = () => {
    onSave({
      justificationText,
      codeBookTitle: codeBookTitle.trim() || undefined,
      chapter: chapter.trim() || undefined,
      snippet: snippet.trim() || undefined,
    });
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
       <DialogContent className="max-w-2xl max-h-[90dvh] md:max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {justification?.justificationText ? 'Edit Justification' : 'Add Justification'}
          </DialogTitle>
          <DialogDescription>
            Provide a justification for this supplement item. Code book references are optional.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Justification Text */}
          <div className="space-y-2">
            <Label htmlFor="justificationText" className="text-sm font-medium">
              Justification <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="justificationText"
              placeholder="Enter the justification text..."
              value={justificationText}
              onChange={(e) => setJustificationText(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Code Book Section */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-3 text-muted-foreground">
              Code Book Reference (Optional)
            </h4>
            
            <div className="space-y-3">
              {/* Code Book Title */}
              <div className="space-y-2">
                <Label htmlFor="codeBookTitle" className="text-sm">
                  Code Book Title
                </Label>
                <Input
                  id="codeBookTitle"
                  placeholder="e.g., Xactimate Price List"
                  value={codeBookTitle}
                  onChange={(e) => setCodeBookTitle(e.target.value)}
                />
              </div>

              {/* Chapter */}
              <div className="space-y-2">
                <Label htmlFor="chapter" className="text-sm">
                  Chapter
                </Label>
                <Input
                  id="chapter"
                  placeholder="e.g., Chapter 5: Roofing"
                  value={chapter}
                  onChange={(e) => setChapter(e.target.value)}
                />
              </div>

              {/* Snippet */}
              <div className="space-y-2">
                <Label htmlFor="snippet" className="text-sm">
                  Snippet / Quote
                </Label>
                <Textarea
                  id="snippet"
                  placeholder="Enter relevant code snippet or quote..."
                  value={snippet}
                  onChange={(e) => setSnippet(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!justificationText.trim()}
          >
            Save Justification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
